import { cn } from "@/lib/utils";
import { formatDateInput, getEarliestBookingDate, isUnavailableBookingDate } from "@/lib/validation";

type CalendarProps = {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  readOnly?: boolean;
  disabled?: boolean;
};

export function Calendar({ value, onChange, id, readOnly, disabled: isCalendarDisabled }: CalendarProps) {
  const earliest = getEarliestBookingDate();
  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const days: Date[] = [];
  const candidate = new Date();
  const dayOfWeek = candidate.getDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  candidate.setDate(candidate.getDate() - daysFromMonday);
  candidate.setHours(0, 0, 0, 0);

  while (days.length < 15) {
    if (!isUnavailableBookingDate(candidate)) {
      days.push(new Date(candidate));
    }

    candidate.setDate(candidate.getDate() + 1);
  }

  return (
    <div className="space-y-3">
      <input id={id} type="hidden" value={value} readOnly disabled={isCalendarDisabled} />
      <div className="grid grid-cols-5 gap-2" aria-readonly={readOnly}>
        {weekDays.map((day) => (
          <span key={day} className="text-xs font-bold text-muted-foreground">
            {day}
          </span>
        ))}
        {days.map((day) => {
          const dateValue = formatDateInput(day);
          const disabled = isCalendarDisabled || day < earliest;
          const selected = value === dateValue;
          const dimmed = disabled && !selected;

          return (
            <button
              key={dateValue}
              type="button"
              disabled={disabled}
              onClick={() => onChange(dateValue)}
              className={cn(
                "min-w-0 rounded-lg border-2 px-2 py-2 text-left text-sm font-bold transition-colors sm:px-3",
                !selected && !disabled && "border-accent/10 bg-white hover:border-accent",
                selected && "border-accent bg-accent text-accent-foreground disabled:opacity-100",
                dimmed && "cursor-not-allowed border-muted bg-muted/60 text-muted-foreground/55 line-through",
              )}
            >
              <span className="block">{day.toLocaleDateString("en-GB", { weekday: "short" })}</span>
              <span className="text-xs">{day.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
